import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Play, Volume2, ExternalLink, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { vibrate } from "../utils/haptics";

/**
 * BlogNewsBox — Apple Wallet–style "Aggiornamenti".
 * - Header centrato: puntino rosso pulsante + occhiello + titolo
 * - Card principale gialla (in evidenza) con ombre
 * - Sotto: stack di card tipo Wallet, si scorrono col dito (drag), molto fluide
 * - Tap su una card → popup di lettura (immagine o video YouTube embeddato)
 */
const LOCALE_MAP = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" };

const BLOG_TITLES = {
  it: { h: "Storie & consigli dalla casa", s: "Il blog della struttura", badge: "Aggiornamenti", more: "Altri aggiornamenti", read: "Leggi tutto", featured: "In evidenza" },
  en: { h: "Stories & tips from the house", s: "The property blog", badge: "Updates", more: "More updates", read: "Read more", featured: "Featured" },
  fr: { h: "Histoires & conseils de la maison", s: "Le blog du logement", badge: "Actualités", more: "Autres actualités", read: "Lire la suite", featured: "À la une" },
  es: { h: "Historias y consejos de la casa", s: "El blog del alojamiento", badge: "Novedades", more: "Más novedades", read: "Leer más", featured: "Destacado" },
  de: { h: "Geschichten & Tipps aus dem Haus", s: "Der Unterkunfts-Blog", badge: "Neuigkeiten", more: "Weitere Neuigkeiten", read: "Weiterlesen", featured: "Highlight" },
};

// Palette morbide per le card Wallet (ciclate)
const CARD_THEMES = [
  { bg: "linear-gradient(135deg,#EFF6FF,#DBEAFE)", chip: "#1D4ED8", ring: "#BFDBFE" },
  { bg: "linear-gradient(135deg,#F0FDF4,#DCFCE7)", chip: "#15803D", ring: "#BBF7D0" },
  { bg: "linear-gradient(135deg,#FDF2F8,#FCE7F3)", chip: "#BE185D", ring: "#FBCFE8" },
  { bg: "linear-gradient(135deg,#F5F3FF,#EDE9FE)", chip: "#6D28D9", ring: "#DDD6FE" },
  { bg: "linear-gradient(135deg,#FFF7ED,#FFEDD5)", chip: "#C2410C", ring: "#FED7AA" },
];

const formatDate = (d, locale = "it-IT") => {
  try { return new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short" }); }
  catch { return ""; }
};
const resolveSrc = (m, apiUrl) => (m && m.startsWith && m.startsWith("http")) ? m : `${apiUrl}/files/${m}`;

const BlogNewsBox = ({ posts = [], apiUrl = "", getYouTubeId = () => null, theme, translations = {} }) => {
  const { lang } = useLanguage();
  const locale = LOCALE_MAP[lang] || "it-IT";
  const TT = BLOG_TITLES[lang] || BLOG_TITLES.it;

  const safePosts = useMemo(() => {
    return (posts || []).slice(0, 6).map((p) => {
      const tr = translations[p.post_id] || {};
      return { ...p, title: tr.title || p.title, subtitle: tr.subtitle || p.subtitle, content: tr.content || p.content };
    });
  }, [posts, translations]);

  const [openPost, setOpenPost] = useState(null);

  const featured = safePosts[0];
  const rest = safePosts.slice(1);
  // Ticket impilati: id della card espansa (accordion in-place)
  const [expandedId, setExpandedId] = useState(null);

  if (safePosts.length === 0) return null;

  const featuredYt = featured.youtube_url ? getYouTubeId(featured.youtube_url) : null;
  const openYt = openPost?.youtube_url ? getYouTubeId(openPost.youtube_url) : null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="relative rounded-[28px] p-5 sm:p-6"
        style={{ background: "#F5F5F7", border: "1px solid #ECECEF" }}
        data-testid="blog-news-box"
      >
        {/* HEADER centrato */}
        <div className="flex justify-center mb-3">
          <div className="inline-flex items-center gap-2" data-testid="blog-live-badge">
            <span className="relative flex h-2.5 w-2.5">
              <motion.span className="absolute inset-0 rounded-full" style={{ background: "#FF3B30" }}
                animate={{ scale: [1, 2.4], opacity: [0.7, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }} />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#FF3B30" }} />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B91C2C" }}>{TT.badge}</span>
          </div>
        </div>
        <div className="text-center mb-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] mb-1" style={{ color: "#E61E4D" }}>{TT.s}</p>
          <h2 className="font-extrabold tracking-tight leading-tight text-xl sm:text-2xl text-zinc-900" style={{ letterSpacing: "-0.02em" }}>{TT.h}</h2>
        </div>

        {/* CARD PRINCIPALE — gialla con ombre */}
        <motion.button
          layout
          onClick={() => setOpenPost(featured)}
          whileTap={{ scale: 0.985 }}
          className="w-full text-left rounded-[26px] overflow-hidden relative"
          style={{ background: "linear-gradient(150deg,#FEF3C7 0%,#FDE68A 55%,#FBCF5B 100%)", boxShadow: "0 22px 40px -18px rgba(245,158,11,0.55), 0 6px 14px -8px rgba(0,0,0,0.15)" }}
          data-testid="blog-featured-card"
        >
          <div className="relative w-full" style={{ aspectRatio: "16/10" }}>
            {featuredYt ? (
              <>
                <img src={`https://img.youtube.com/vi/${featuredYt}/hqdefault.jpg`} alt={featured.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                    <Play className="w-6 h-6 text-zinc-900 ml-0.5" fill="currentColor" />
                  </span>
                </div>
              </>
            ) : featured.photo ? (
              <img src={resolveSrc(featured.photo, apiUrl)} alt={featured.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full" style={{ background: "linear-gradient(135deg,#FDE68A,#FBBF24)" }} />
            )}
            <span className="absolute top-3 left-3 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-zinc-900/85 text-white backdrop-blur">
              ★ {TT.featured}
            </span>
          </div>
          <div className="p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#B45309" }}>{formatDate(featured.created_at, locale)}</p>
            <h3 className="mt-1 text-lg font-extrabold leading-tight text-zinc-900 line-clamp-2" style={{ letterSpacing: "-0.01em" }}>{featured.title}</h3>
            {(featured.subtitle || featured.content) && (
              <p className="mt-1.5 text-[13px] text-zinc-700/90 leading-snug line-clamp-2">{featured.subtitle || featured.content}</p>
            )}
            <span className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-zinc-900 text-white shadow-md">
              {TT.read} <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </motion.button>

        {/* MAZZO DI CARTE — impilate una sull'altra, si espandono al tap (stile Welcome Book) */}
        {rest.length > 0 && (
          <motion.div layout className="relative flex flex-col mt-3" data-testid="blog-ticket-stack">
              {rest.map((p, i) => {
                const themeC = CARD_THEMES[i % CARD_THEMES.length];
                const isOpen = expandedId === p.post_id;
                const anyOpen = expandedId !== null;
                const n = rest.length;
                const yt = p.youtube_url ? getYouTubeId(p.youtube_url) : null;
                return (
                  <motion.div
                    layout
                    key={p.post_id}
                    onClick={() => { if (!isOpen) vibrate(10); setExpandedId(isOpen ? null : p.post_id); }}
                    className="rounded-[20px] overflow-hidden cursor-pointer relative"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #EDEDF0",
                      transformOrigin: "top center",
                      zIndex: anyOpen ? (isOpen ? 50 : 1) : (n - i),
                      boxShadow: isOpen ? "0 22px 40px -18px rgba(0,0,0,0.4)" : "0 10px 24px -12px rgba(0,0,0,0.35)",
                    }}
                    animate={{ marginTop: i === 0 ? 0 : (anyOpen ? 12 : -44), scale: anyOpen ? 1 : 1 - Math.min(i, 3) * 0.05 }}
                    transition={{ type: "spring", stiffness: 340, damping: 32 }}
                  >
                    {/* Header ticket (sempre visibile) — centrato e minimal */}
                    <div className="relative px-9 py-3 text-center">
                      <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">{formatDate(p.created_at, locale)}{yt ? " · Video" : ""}</p>
                      <p className={`text-[13px] font-bold text-zinc-900 leading-tight ${isOpen ? "" : "line-clamp-1"}`}>{p.title}</p>
                      <motion.span animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }} className="absolute right-3 top-1/2 -translate-y-1/2">
                        <ChevronRight className="w-4 h-4 text-zinc-300" />
                      </motion.span>
                    </div>

                    {/* Contenuto espanso */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.28, ease: "easeInOut" }}
                          className="px-3 pb-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="rounded-2xl overflow-hidden bg-white/70 border" style={{ borderColor: "#EDEDF0" }}>
                            {yt ? (
                              <div className="relative w-full bg-black" style={{ aspectRatio: "16/9" }}>
                                <iframe src={`https://www.youtube.com/embed/${yt}`} title={p.title} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                              </div>
                            ) : p.photo ? (
                              <img src={resolveSrc(p.photo, apiUrl)} alt={p.title} className="w-full object-cover" style={{ maxHeight: 180 }} />
                            ) : null}
                            <div className="p-3 text-center">
                              {p.subtitle && <p className="text-[13px] font-semibold text-zinc-600 leading-snug">{p.subtitle}</p>}
                              {p.content && <p className="mt-1 text-[13px] text-zinc-700 leading-relaxed whitespace-pre-line">{p.content}</p>}
                              <div className="flex flex-wrap gap-2 mt-3 justify-center">
                                <button onClick={(e) => { e.stopPropagation(); setOpenPost(p); }} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-zinc-900 text-white">
                                  {TT.read} <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                                {p.link_url && (
                                  <a href={p.link_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: "#FF385C" }}>
                                    {p.link_label || "Link"} <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
          </motion.div>
        )}
        {rest.length > 1 && expandedId === null && (
          <p className="text-center text-[11px] font-medium text-zinc-400 mt-2.5" data-testid="blog-more-count">
            +{rest.length - 1} {lang === "fr" ? "autres" : lang === "es" ? "más" : lang === "de" ? "weitere" : lang === "en" ? "more" : "altri"}
          </p>
        )}
      </motion.div>

      {/* READ-MORE POPUP */}
      <AnimatePresence>
        {openPost && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6"
            style={{ background: "rgba(9,9,11,0.6)", backdropFilter: "blur(6px)" }}
            onClick={() => setOpenPost(null)} data-testid="blog-post-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full md:max-w-2xl max-h-[92vh] md:max-h-[88vh] overflow-y-auto bg-white rounded-t-3xl md:rounded-3xl shadow-2xl"
            >
              <button onClick={() => setOpenPost(null)} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-white/90 text-zinc-700 hover:bg-white shadow-md" aria-label="Chiudi" data-testid="blog-modal-close">
                <X className="w-5 h-5" />
              </button>
              {openYt ? (
                <div className="relative w-full bg-black" style={{ aspectRatio: "16/9" }}>
                  <iframe src={`https://www.youtube.com/embed/${openYt}`} title={openPost.title} className="absolute inset-0 w-full h-full rounded-t-3xl" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="blog-modal-video" />
                </div>
              ) : openPost.photo ? (
                <img src={resolveSrc(openPost.photo, apiUrl)} alt={openPost.title} className="w-full object-cover rounded-t-3xl" style={{ maxHeight: "42vh" }} />
              ) : null}
              <div className="p-6 md:p-8">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: "#E61E4D" }}>{formatDate(openPost.created_at, locale)}</p>
                <h3 className="text-2xl md:text-3xl font-black tracking-tight leading-tight text-zinc-900" style={{ letterSpacing: "-0.03em" }}>{openPost.title}</h3>
                {openPost.subtitle && <p className="mt-2 text-base md:text-lg font-semibold text-zinc-500 leading-relaxed">{openPost.subtitle}</p>}
                {openPost.content && <p className="mt-4 text-[15px] text-zinc-700 leading-7 whitespace-pre-line">{openPost.content}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-6">
                  {openPost.audio_url && (
                    <a href={openPost.audio_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-zinc-100 text-zinc-800 hover:bg-zinc-200">
                      <Volume2 className="w-4 h-4" /> Audio
                    </a>
                  )}
                  {openPost.link_url && (
                    <a href={openPost.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-black text-white" style={{ background: "#FF385C" }}>
                      {openPost.link_label || "Apri link"} <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BlogNewsBox;
